import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building, Car, Home, Heart, DollarSign, Briefcase,
  ArrowRight, ArrowLeft, Check, Zap, TrendingUp,
  GraduationCap, PiggyBank, Receipt, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MILEAGE_RATE = 0.67;    // display only — used in informational text, not financial computation
const HOME_OFFICE_RATE = 5.0; // display only — used in informational text
const TOTAL_STEPS = 5;

const BUSINESS_CATEGORIES = [
  { id: 'freelancer',  label: 'Freelancer / Consultant', Icon: Briefcase },
  { id: 'agency',      label: 'Agency Owner',            Icon: Building },
  { id: 'creator',     label: 'Content Creator',         Icon: Zap },
  { id: 'contractor',  label: '1099 Contractor',         Icon: Receipt },
  { id: 'ecommerce',   label: 'E-Commerce',              Icon: TrendingUp },
  { id: 'other',       label: 'Other Self-Employed',     Icon: DollarSign },
];

const REVENUE_RANGES = [
  { id: '<50k',       label: 'Under $50K'       },
  { id: '50k_100k',   label: '$50K – $100K'     },
  { id: '100k_200k',  label: '$100K – $200K'    },
  { id: '200k_300k',  label: '$200K – $300K'    },
  { id: '300k_plus',  label: '$300K+'           },
];

const BENEFIT_CARDS = [
  {
    key:   'has_health_insurance',
    Icon:  Heart,
    title: 'Self-paid health insurance',
    desc:  'Health insurance premiums may be deductible for self-employed individuals — eligibility depends on net profit and whether you have access to employer coverage.',
    note:  'Avg. $3,000–$15,000/yr deductible',
    color: 'pink',
  },
  {
    key:   'has_retirement',
    Icon:  PiggyBank,
    title: 'SEP-IRA or Solo 401(k)',
    desc:  'Contribute up to 25% of net income — reduces both SE tax and income tax.',
    note:  'Up to $69,000/yr fully deductible',
    color: 'blue',
  },
  {
    key:   'has_education_expenses',
    Icon:  GraduationCap,
    title: 'Courses, software, or certifications',
    desc:  'Work-related education and professional development is 100% deductible.',
    note:  'Fully deductible as business expense',
    color: 'purple',
  },
];

const OTHER_INCOME_CARDS = [
  {
    key:   'has_investment_income',
    title: 'Investment income (1099-INT / 1099-DIV)',
    desc:  'Interest, dividends, or capital gains from investments.',
  },
  {
    key:   'has_prior_year_refund',
    title: 'Received a tax refund last year',
    desc:  'May affect your current-year estimated tax calculations.',
  },
  {
    key:   'has_w2_income',
    title: 'Also have W-2 employment income',
    desc:  'You have a regular employer in addition to self-employment.',
  },
];

const formatCurrency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);

export default function Intake() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const [step, setStep]         = useState(1);
  const [saving, setSaving]     = useState(false);
  const [states, setStates]     = useState([]);
  const [auditResults, setAuditResults] = useState(null);

  const [answers, setAnswers] = useState({
    business_category:    '',
    annual_revenue_range: '',
    filing_status:        'single',
    state:                '',
    works_from_home:      null,
    home_office_sqft:     200,
    uses_vehicle:         null,
    business_miles:       5000,
    has_health_insurance: null,
    has_retirement:       null,
    retirement_amount:    10000,
    retirement_type:      'sep_ira',
    has_education_expenses: null,
    has_investment_income:  false,
    has_prior_year_refund:  false,
    has_w2_income:          false,
  });

  const set = (key, val) => setAnswers(a => ({ ...a, [key]: val }));

  // Load states + existing intake
  useEffect(() => {
    axios.get(`${API}/states`).then(r => setStates(r.data.states || [])).catch(() => {});
    axios.get(`${API}/user/intake`, { headers: getAuthHeaders() })
      .then(r => {
        if (r.data.intake_data && Object.keys(r.data.intake_data).length > 0) {
          setAnswers(prev => ({ ...prev, ...r.data.intake_data }));
        }
      })
      .catch(() => {});
  }, []);

  // ── Savings preview (Commandment II): fetched from backend, not computed here ──
  const [savingsPreview, setSavingsPreview] = useState({
    home_office_savings: 0, vehicle_savings: 0,
    health_savings: 0, retirement_savings: 0, total_preview_savings: 0,
  });

  useEffect(() => {
    const payload = {
      annual_revenue:          answers.annual_revenue_range ? parseFloat(answers.annual_revenue_range) || null : null,
      home_office_sqft:        answers.works_from_home ? (parseFloat(answers.home_office_sqft) || null) : null,
      annual_mileage:          answers.uses_vehicle ? (parseFloat(answers.business_miles) || null) : null,
      monthly_health_premium:  answers.has_health_insurance ? 600 : null,  // $600/mo default if selected
      monthly_retirement:      answers.has_retirement ? (parseFloat(answers.retirement_amount) || 0) / 12 : null,
    };
    axios.post(`${API}/user/intake/savings-preview`, payload).then(r => {
      setSavingsPreview(r.data);
    }).catch(() => {});
  }, [answers.works_from_home, answers.home_office_sqft, answers.uses_vehicle,
      answers.business_miles, answers.has_health_insurance, answers.has_retirement,
      answers.retirement_amount]);

  const homeOfficeSavings   = savingsPreview.home_office_savings;
  const vehicleSavings      = savingsPreview.vehicle_savings;
  const healthSavings       = savingsPreview.health_savings;
  const retirementSavings   = savingsPreview.retirement_savings;
  const totalPreviewSavings = savingsPreview.total_preview_savings;

  // ── Step validation ──────────────────────────────────────────────────────────
  const canProceed = () => {
    if (step === 1) return !!(answers.business_category && answers.annual_revenue_range);
    if (step === 2) return answers.works_from_home !== null && !!answers.filing_status && !!answers.state;
    if (step === 3) return answers.uses_vehicle !== null;
    if (step === 4) return answers.has_health_insurance !== null
                        && answers.has_retirement !== null
                        && answers.has_education_expenses !== null;
    return true;
  };

  const handleNext = () => {
    if (step < TOTAL_STEPS) { setStep(s => s + 1); }
    else { handleComplete(); }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/user/intake`, answers, { headers: getAuthHeaders() });
      const year = new Date().getFullYear();
      const auditRes = await axios.get(`${API}/tax-audit/quick-scan/${year}`, { headers: getAuthHeaders() });
      setAuditResults(auditRes.data);
      setStep(6);
    } catch {
      toast.error('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Shared style helpers ─────────────────────────────────────────────────────
  const optionCls = (active) =>
    `p-4 rounded-xl border-2 text-center transition-all cursor-pointer ${
      active ? 'border-emerald-400 bg-emerald-500/20 text-white'
             : 'border-white/20 bg-white/5 text-slate-300 hover:border-white/40'
    }`;

  // ── Completion screen ────────────────────────────────────────────────────────
  if (step === 6) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 flex items-center justify-center p-6" data-testid="intake-completion">
        <div className="max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Your Tax Audit is Personalized</h1>
          <p className="text-slate-300 mb-8">Based on your answers, we identified your top savings opportunities.</p>

          {auditResults && (
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-white/20 text-left">
              <p className="text-emerald-300 text-sm font-medium mb-1 text-center">Estimated Annual Tax Savings</p>
              <p className="text-5xl font-bold text-white mb-5 text-center" data-testid="intake-total-savings">
                {formatCurrency(auditResults.total_potential_savings)}
              </p>
              <div className="space-y-2">
                {auditResults.top_opportunities?.map((opp, idx) => {
                  const saving = opp.potential_tax_savings || opp.annual_tax_savings || 0;
                  return (
                    <div key={idx} className="flex justify-between text-sm py-1 border-b border-white/10">
                      <span className="text-slate-300 capitalize">{opp.opportunity?.replace(/_/g, ' ')}</span>
                      <span className="text-emerald-300 font-semibold">{formatCurrency(saving)}/yr</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <Button
            className="w-full bg-emerald-500 hover:bg-emerald-400 text-white py-6 text-lg font-semibold"
            onClick={() => navigate('/dashboard')}
            data-testid="intake-go-to-dashboard"
          >
            Go to Your Dashboard
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>
    );
  }

  // ── Main wizard ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900" data-testid="intake-page">

      {/* ── Header ── */}
      <div className="max-w-2xl mx-auto px-4 pt-8 pb-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 relative flex items-center justify-center">
              <div className="absolute inset-0 bg-emerald-500 rounded-lg transform rotate-45" />
              <div className="relative w-3 h-3 bg-white z-10" />
            </div>
            <span className="text-white font-semibold">SolelyTax</span>
          </div>
          <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
            Step {step} of {TOTAL_STEPS}
          </Badge>
        </div>

        <Progress value={(step / TOTAL_STEPS) * 100} className="h-1.5 bg-white/10 mb-2" data-testid="intake-progress" />
        <p className="text-xs text-slate-500 text-right mb-6">{Math.round((step / TOTAL_STEPS) * 100)}% complete</p>

        {/* Savings ticker */}
        {totalPreviewSavings > 0 && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 mb-6 flex items-center justify-between" data-testid="savings-ticker">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-300 text-sm font-medium">Savings identified so far</span>
            </div>
            <span className="text-emerald-300 text-lg font-bold">{formatCurrency(totalPreviewSavings)}/yr</span>
          </div>
        )}
      </div>

      {/* ── Step content ── */}
      <div className="max-w-2xl mx-auto px-4 pb-32">

        {/* ── STEP 1: Business Profile ── */}
        {step === 1 && (
          <div data-testid="intake-step-1">
            <h1 className="text-2xl font-bold text-white mb-1">What best describes your work?</h1>
            <p className="text-slate-400 mb-6">This sets the baseline for your personalized audit.</p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
              {BUSINESS_CATEGORIES.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  onClick={() => set('business_category', id)}
                  className={optionCls(answers.business_category === id)}
                  data-testid={`business-category-${id}`}
                >
                  <Icon className="w-5 h-5 mx-auto mb-2" />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>

            <h2 className="text-lg font-semibold text-white mb-3">Estimated annual revenue</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {REVENUE_RANGES.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => set('annual_revenue_range', id)}
                  className={optionCls(answers.annual_revenue_range === id)}
                  data-testid={`revenue-range-${id}`}
                >
                  <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── STEP 2: Home + Filing ── */}
        {step === 2 && (
          <div className="space-y-6" data-testid="intake-step-2">
            <div>
              <h1 className="text-2xl font-bold text-white mb-1">Do you work from home?</h1>
              <p className="text-slate-400 mb-4">Home office deductions are commonly available to solopreneurs but often go unclaimed.</p>

              <div className="grid grid-cols-2 gap-3 mb-4">
                {[{ val: true, label: 'Yes, I work from home', Icon: Home }, { val: false, label: 'No, I work elsewhere', Icon: Building }].map(({ val, label, Icon }) => (
                  <button
                    key={String(val)}
                    onClick={() => set('works_from_home', val)}
                    className={optionCls(answers.works_from_home === val)}
                    data-testid={`works-from-home-${val}`}
                  >
                    <Icon className="w-6 h-6 mx-auto mb-1" />
                    <span className="text-sm font-medium">{label}</span>
                  </button>
                ))}
              </div>

              {answers.works_from_home && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <div className="flex justify-between items-center mb-3">
                    <Label className="text-white font-medium">Home office size</Label>
                    <span className="text-emerald-400 font-bold" data-testid="sqft-display">{answers.home_office_sqft} sq ft</span>
                  </div>
                  <Slider
                    min={50} max={300} step={10}
                    value={[answers.home_office_sqft]}
                    onValueChange={([v]) => set('home_office_sqft', v)}
                    className="mb-4"
                    data-testid="home-office-sqft-slider"
                  />
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-center">
                    <p className="text-xs text-emerald-300 mb-1">Potential deduction</p>
                    <p className="text-xl font-bold text-emerald-300">
                      {formatCurrency(answers.home_office_sqft * HOME_OFFICE_RATE)}
                      <span className="text-sm font-normal text-emerald-400"> → {formatCurrency(homeOfficeSavings)} saved</span>
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white mb-2 block">Filing status</Label>
                <Select value={answers.filing_status} onValueChange={v => set('filing_status', v)}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="intake-filing-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single</SelectItem>
                    <SelectItem value="married_filing_jointly">Married Filing Jointly</SelectItem>
                    <SelectItem value="married_filing_separately">Married Filing Separately</SelectItem>
                    <SelectItem value="head_of_household">Head of Household</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white mb-2 block">State of residence</Label>
                <Select value={answers.state} onValueChange={v => set('state', v)}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="intake-state">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent className="max-h-48 overflow-y-auto">
                    {states.map(s => (
                      <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 3: Vehicle ── */}
        {step === 3 && (
          <div className="space-y-5" data-testid="intake-step-3">
            <div>
              <h1 className="text-2xl font-bold text-white mb-1">Do you use a vehicle for business?</h1>
              <p className="text-slate-400 mb-4">
                Business mileage is deductible at {(MILEAGE_RATE * 100).toFixed(0)}¢/mile — most freelancers leave this on the table.
              </p>

              <div className="grid grid-cols-2 gap-3 mb-4">
                {[{ val: true, label: 'Yes, for business' }, { val: false, label: 'No vehicle use' }].map(({ val, label }) => (
                  <button
                    key={String(val)}
                    onClick={() => set('uses_vehicle', val)}
                    className={optionCls(answers.uses_vehicle === val)}
                    data-testid={`uses-vehicle-${val}`}
                  >
                    <Car className="w-6 h-6 mx-auto mb-1" />
                    <span className="text-sm font-medium">{label}</span>
                  </button>
                ))}
              </div>

              {answers.uses_vehicle && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <div className="flex justify-between items-center mb-3">
                    <Label className="text-white font-medium">Business miles per year</Label>
                    <span className="text-emerald-400 font-bold" data-testid="miles-display">{answers.business_miles.toLocaleString()} mi</span>
                  </div>
                  <Slider
                    min={500} max={30000} step={500}
                    value={[answers.business_miles]}
                    onValueChange={([v]) => set('business_miles', v)}
                    className="mb-4"
                    data-testid="business-miles-slider"
                  />
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-center">
                    <p className="text-xs text-emerald-300 mb-1">Potential mileage deduction</p>
                    <p className="text-xl font-bold text-emerald-300">
                      {formatCurrency(answers.business_miles * MILEAGE_RATE)}
                      <span className="text-sm font-normal text-emerald-400"> → {formatCurrency(vehicleSavings)} saved</span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── STEP 4: Benefits ── */}
        {step === 4 && (
          <div className="space-y-4" data-testid="intake-step-4">
            <div>
              <h1 className="text-2xl font-bold text-white mb-1">Let's find your benefits deductions</h1>
              <p className="text-slate-400 mb-5">Three major deductions most solopreneurs underuse.</p>
            </div>

            {BENEFIT_CARDS.map(({ key, Icon, title, desc, note, color }) => (
              <div key={key} className="bg-white/5 rounded-xl p-4 border border-white/10" data-testid={`benefit-card-${key}`}>
                <div className="flex items-start gap-3 mb-3">
                  <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 text-${color}-400`} />
                  <div>
                    <p className="text-white font-medium">{title}</p>
                    <p className="text-slate-400 text-sm">{desc}</p>
                    <p className="text-emerald-400 text-xs mt-1">{note}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[{ val: true, label: 'Yes' }, { val: false, label: 'No' }].map(({ val, label }) => (
                    <button
                      key={String(val)}
                      onClick={() => set(key, val)}
                      className={`py-2 rounded-lg border text-sm font-medium transition-all ${
                        answers[key] === val
                          ? 'border-emerald-400 bg-emerald-500/20 text-white'
                          : 'border-white/20 text-slate-400 hover:border-white/40'
                      }`}
                      data-testid={`${key}-${val}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {key === 'has_retirement' && answers.has_retirement && (
                  <div className="mt-3 space-y-2">
                    <Label className="text-white text-sm">Annual contribution amount</Label>
                    <div className="flex gap-3">
                      <Input
                        type="number"
                        placeholder="10000"
                        value={answers.retirement_amount || ''}
                        onChange={e => set('retirement_amount', parseFloat(e.target.value) || 0)}
                        className="bg-white/10 border-white/20 text-white placeholder:text-slate-500"
                        data-testid="retirement-amount"
                      />
                      <Select value={answers.retirement_type} onValueChange={v => set('retirement_type', v)}>
                        <SelectTrigger className="bg-white/10 border-white/20 text-white w-36">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sep_ira">SEP-IRA</SelectItem>
                          <SelectItem value="solo_401k">Solo 401(k)</SelectItem>
                          <SelectItem value="simple_ira">SIMPLE IRA</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── STEP 5: Other Income ── */}
        {step === 5 && (
          <div className="space-y-4" data-testid="intake-step-5">
            <div>
              <h1 className="text-2xl font-bold text-white mb-1">Any other income to account for?</h1>
              <p className="text-slate-400 mb-5">Helps us build your complete tax picture.</p>
            </div>

            {OTHER_INCOME_CARDS.map(({ key, title, desc }) => (
              <div key={key} className="bg-white/5 rounded-xl p-4 border border-white/10" data-testid={`income-card-${key}`}>
                <div className="mb-3">
                  <p className="text-white font-medium">{title}</p>
                  <p className="text-slate-400 text-sm">{desc}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[{ val: true, label: 'Yes' }, { val: false, label: 'No' }].map(({ val, label }) => (
                    <button
                      key={String(val)}
                      onClick={() => set(key, val)}
                      className={`py-2 rounded-lg border text-sm font-medium transition-all ${
                        answers[key] === val
                          ? 'border-emerald-400 bg-emerald-500/20 text-white'
                          : 'border-white/20 text-slate-400 hover:border-white/40'
                      }`}
                      data-testid={`${key}-${val}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Fixed navigation footer ── */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-sm border-t border-white/10 p-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-4">
          {step > 1 ? (
            <Button
              variant="ghost"
              onClick={() => setStep(s => s - 1)}
              className="text-slate-400 hover:text-white"
              data-testid="intake-back"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          ) : <div />}

          <Button
            onClick={handleNext}
            disabled={!canProceed() || saving}
            className="bg-emerald-500 hover:bg-emerald-400 text-white px-8 py-3 text-base font-semibold flex-1 max-w-xs"
            data-testid="intake-next"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Personalizing audit...</>
            ) : step === TOTAL_STEPS ? (
              <>Run My Tax Audit <ArrowRight className="ml-2 w-4 h-4" /></>
            ) : (
              <>Next <ArrowRight className="ml-2 w-4 h-4" /></>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
